import { MASTER_PART_TYPE } from "./designerUtils";

export const PRINT_INSTANCE_SEPARATOR = "::";
export const DEFAULT_PRINT_BASE_SIZE_CM = 4;
export const BASE_CANVAS_CM = 94;
export const TECHNIQUE_SWITCH_THRESHOLD_CM = 4;
export const PRINT_SCALE_MIN = 30;
export const PRINT_SCALE_MAX = 600;
// Los assets del diseñador (prints, máscaras, fuentes) los sirve V4: se copiaron a su public/
// el 2-10-2026 para poder apagar v3core. `REACT_APP_ASSET_URL` permite moverlos sin recompilar.
const CDN_BASE = process.env.REACT_APP_ASSET_URL || "https://v4.micuenta.blue";
const FIXED_PREDESIGN_SUBTYPES = new Set([5, 8]);
export const PREDESIGN_SIZE_TIERS = Object.freeze({
    SMALL: "small",
    MEDIUM: "medium",
    LARGE: "large",
});
export const PREDESIGN_SIZE_CM_BY_TIER = Object.freeze({
    [PREDESIGN_SIZE_TIERS.SMALL]: 8,
    [PREDESIGN_SIZE_TIERS.MEDIUM]: 15,
    [PREDESIGN_SIZE_TIERS.LARGE]: 22,
});
const PRINT_SIZE_SMALL_MAX_CM = 4.5;
const PRINT_SIZE_MEDIUM_MAX_CM = 8.5;
const normalizeVisibilityText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export const PRINT_NODE_TYPE = Number(MASTER_PART_TYPE.PRINTS);
export const GROUP_OPTION_TYPES = new Set([
    Number(MASTER_PART_TYPE.PART_TYPE),
    Number(MASTER_PART_TYPE.PART_PART),
    Number(MASTER_PART_TYPE.PART_LEFT),
    Number(MASTER_PART_TYPE.PART_RIGHT),
]);

export const MODAL_DEPENDENT_TYPES = Object.freeze({
    [Number(MASTER_PART_TYPE.PART_TYPE)]: new Set([
        Number(MASTER_PART_TYPE.PART_PART),
        Number(MASTER_PART_TYPE.PART_LEFT),
        Number(MASTER_PART_TYPE.PART_RIGHT),
    ]),
    [Number(MASTER_PART_TYPE.PART_PART)]: new Set([
        Number(MASTER_PART_TYPE.PART_TYPE),
        Number(MASTER_PART_TYPE.PART_LEFT),
        Number(MASTER_PART_TYPE.PART_RIGHT),
    ]),
    [Number(MASTER_PART_TYPE.EXTRA)]: new Set([Number(MASTER_PART_TYPE.PART_PART)]),
});

export const sanitizePrintLocationLabel = (value, fallback = "Diseño") => {
    const clean = String(value || "").trim();
    if (!clean) return fallback;
    if (/^\d+$/.test(clean)) return fallback;
    const withoutNumericLocation = clean.replace(/ubicaci[oó]n\s*\d+/gi, "diseño");
    const withoutIds = withoutNumericLocation.replace(/\b\d+\b/g, "").replace(/\s{2,}/g, " ").trim();
    return withoutIds || fallback;
};

export const isClosureLabel = (value) => /cierre|zipper/i.test(String(value || ""));

export const isMetalMaterial = (material) => /metal/i.test(String(material?.name || material?.label || ""));

export const filterMaterialsForPart = (partLabel, materials = []) => {
    if (!isClosureLabel(partLabel)) return materials.filter(Boolean);
    return materials.filter((material) => material && !isMetalMaterial(material));
};

export const normalizeColorCode = (value) => {
    if (!value) return value;
    const raw = String(value).trim();
    if (!raw) return raw;
    if (raw.startsWith("#")) return raw;
    if (/^[0-9a-fA-F]{3,8}$/.test(raw)) return `#${raw}`;
    return raw;
};

export const normalizeColorObject = (colorObj) => {
    if (!colorObj) return colorObj;
    const resolvedCode =
        colorObj.code
        || colorObj.hex
        || colorObj.color_hex
        || colorObj.color
        || null;
    return {
        ...colorObj,
        code: normalizeColorCode(resolvedCode),
    };
};

export const buildPrintInstanceKey = (locationKey) =>
    `${String(locationKey)}${PRINT_INSTANCE_SEPARATOR}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getPrintInstanceOrder = (printKey, selection = null) => {
    const rawKey = String(selection?.attrs?.print_instance_key || printKey || "");
    const suffix = rawKey.includes(PRINT_INSTANCE_SEPARATOR)
        ? rawKey.split(PRINT_INSTANCE_SEPARATOR)[1]
        : rawKey;
    const match = String(suffix).match(/^(\d{10,})/);
    const timestamp = Number(match?.[1] || 0);
    return Number.isFinite(timestamp) ? timestamp : 0;
};

export const buildSyntheticPrintForMode = (locationKey, mode, basePrint = null) => ({
    id: basePrint?.id || `custom-${String(locationKey)}-${mode}-${Date.now()}`,
    factory_name: mode === "text" ? "Texto personalizado" : "Imagen personalizada",
    nice_name: mode === "text" ? "Texto personalizado" : "Imagen personalizada",
    name: mode === "text" ? "Texto personalizado" : "Imagen personalizada",
    master_print_type: Number(basePrint?.master_print_type || 1),
    max_letters: mode === "text" ? 18 : 0,
});

export const parsePositiveNumberOrNull = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
};

export const getSelectionBaseWidthCm = (selection) => {
    if (!selection) return DEFAULT_PRINT_BASE_SIZE_CM;
    const attrs = selection.attrs || {};
    const print = selection.print || {};
    return (
        parsePositiveNumberOrNull(attrs.max_ancho)
        || parsePositiveNumberOrNull(attrs.maxw)
        || parsePositiveNumberOrNull(print.max_width)
        || parsePositiveNumberOrNull(print.max_ancho)
        || parsePositiveNumberOrNull(print.maxw)
        || DEFAULT_PRINT_BASE_SIZE_CM
    );
};

export const getSelectionBaseHeightCm = (selection) => {
    if (!selection) return DEFAULT_PRINT_BASE_SIZE_CM;
    const attrs = selection.attrs || {};
    const print = selection.print || {};
    return (
        parsePositiveNumberOrNull(attrs.base_height_cm)
        || parsePositiveNumberOrNull(attrs.rendered_height_cm)
        || parsePositiveNumberOrNull(attrs.height)
        || parsePositiveNumberOrNull(print.max_height)
        || parsePositiveNumberOrNull(print.min_height)
        || DEFAULT_PRINT_BASE_SIZE_CM
    );
};

export const getSelectionScalePct = (selection, overrideScalePct = null) => {
    const fallbackScalePct = isFixedPredesignSelection(selection)
        ? getPredesignScalePct(selection, selection?.attrs?.predesign_size_tier)
        : 100;
    return (
        parsePositiveNumberOrNull(overrideScalePct)
        || parsePositiveNumberOrNull(selection?.attrs?.scale_pct)
        || fallbackScalePct
    );
};

export const getSelectionEffectiveSizeCm = (selection) => {
    const baseWidthCm = getSelectionBaseWidthCm(selection);
    const scalePct = getSelectionScalePct(selection);
    const effectiveWidthCm = baseWidthCm * (scalePct / 100);
    return Math.max(0.01, Math.min(BASE_CANVAS_CM, effectiveWidthCm));
};

export const getSelectionEffectiveDimensionsCm = (
    selection,
    {
        aspectRatioOverride = null,
        scalePctOverride = null,
    } = {}
) => {
    const attrs = selection?.attrs || {};
    const mode = String(selection?.mode || "").toLowerCase();
    const printKind = String(attrs?.print_kind || "").toLowerCase();
    const isTextLike = printKind === "text" || mode === "text";
    const scalePct = getSelectionScalePct(selection, scalePctOverride);
    const baseWidthCm = Math.max(0.01, getSelectionBaseWidthCm(selection));
    const scaledReferenceCm = Math.max(0.01, baseWidthCm * (scalePct / 100));

    if (isTextLike) {
        const baseHeightCm = Math.max(0.01, getSelectionBaseHeightCm(selection));
        const effectiveHeightCm = Math.max(0.01, baseHeightCm * (scalePct / 100));
        return {
            width: scaledReferenceCm,
            height: effectiveHeightCm,
        };
    }

    const aspectRatio = Math.max(0.05, parsePositiveNumberOrNull(aspectRatioOverride) || 1);
    if (aspectRatio >= 1) {
        return {
            width: scaledReferenceCm,
            height: Math.max(0.01, scaledReferenceCm / aspectRatio),
        };
    }

    return {
        width: Math.max(0.01, scaledReferenceCm * aspectRatio),
        height: scaledReferenceCm,
    };
};

export const isPredesignPrint = (print = null) => {
    const subtype = Number(print?.master_print_subtype ?? print?.subtype ?? 0);
    if (FIXED_PREDESIGN_SUBTYPES.has(subtype)) return true;
    const haystack = String(print?.name || print?.nice_name || print?.factory_name || "").toLowerCase();
    return haystack.includes("predise");
};

// ¿Se puede pintar (elegir color) este print? Flag explícito por escudo
// (master_prints.tintable, expuesto en el payload): 1 = el cliente elige color · 0 = respeta los
// colores del diseño (ej. bandera argentina full-color). Si el flag no viene, se cae al criterio
// viejo por subtype (prediseño 5/8 + escudo 6) o nombre "predise".
const TINTABLE_SUBTYPES = new Set([5, 6, 8]);
export const isTintablePrint = (print = null) => {
    const flag = print?.tintable ?? print?.can_tint ?? null;
    if (flag != null) return Number(flag) === 1;
    const subtype = Number(print?.master_print_subtype ?? print?.subtype ?? 0);
    if (TINTABLE_SUBTYPES.has(subtype)) return true;
    const haystack = String(print?.name || print?.nice_name || print?.factory_name || "").toLowerCase();
    return haystack.includes("predise");
};

export const isFixedPredesignSelection = (selection = null) => {
    const attrs = selection?.attrs || {};
    const mode = String(selection?.mode || "").toLowerCase();
    const printKind = String(attrs?.print_kind || "").toLowerCase();
    if (printKind === "predesign" || mode === "predesign") return true;
    if (String(attrs?.image_url || "").trim()) return false;
    return isPredesignPrint(selection?.print);
};

export const normalizePredesignSizeTier = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === PREDESIGN_SIZE_TIERS.SMALL) return PREDESIGN_SIZE_TIERS.SMALL;
    if (normalized === PREDESIGN_SIZE_TIERS.LARGE) return PREDESIGN_SIZE_TIERS.LARGE;
    return PREDESIGN_SIZE_TIERS.MEDIUM;
};

export const getPredesignTargetSizeCm = (tier = null) =>
    Number(PREDESIGN_SIZE_CM_BY_TIER[normalizePredesignSizeTier(tier)] || PREDESIGN_SIZE_CM_BY_TIER[PREDESIGN_SIZE_TIERS.MEDIUM]);

const resolvePredesignSelection = (source = {}, fallbackPrint = null) => {
    if (source?.attrs || source?.print) return source;
    return {
        attrs: source || {},
        print: fallbackPrint || null,
    };
};

export const getPredesignScalePct = (source = {}, tier = null, fallbackPrint = null) => {
    const selection = resolvePredesignSelection(source, fallbackPrint);
    const normalizedTier = normalizePredesignSizeTier(
        tier
        || selection?.attrs?.predesign_size_tier
        || selection?.predesign_size_tier
    );
    const baseWidthCm = Math.max(0.01, getSelectionBaseWidthCm(selection));
    const targetWidthCm = getPredesignTargetSizeCm(normalizedTier);
    return Math.max(1, Number(((targetWidthCm / baseWidthCm) * 100).toFixed(2)));
};

export const getPredesignSizeLabel = (tier = null) => {
    const normalizedTier = normalizePredesignSizeTier(tier);
    if (normalizedTier === PREDESIGN_SIZE_TIERS.SMALL) return "Pequeño";
    if (normalizedTier === PREDESIGN_SIZE_TIERS.LARGE) return "Grande";
    return "Mediano";
};

export const applyPredesignSizeAttrs = (source = {}, tier = null, fallbackPrint = null) => {
    const selection = resolvePredesignSelection(source, fallbackPrint);
    const attrs = selection?.attrs || {};
    const normalizedTier = normalizePredesignSizeTier(tier || attrs?.predesign_size_tier);
    const targetHeightCm = getPredesignTargetSizeCm(normalizedTier);
    return {
        ...attrs,
        predesign_size_tier: normalizedTier,
        scale_pct: String(getPredesignScalePct(selection, normalizedTier, fallbackPrint)),
        height: String(targetHeightCm),
    };
};

export const getPrintSizeLabelFromCm = (sizeCm) => {
    const normalized = parsePositiveNumberOrNull(sizeCm) || 0;
    if (normalized <= PRINT_SIZE_SMALL_MAX_CM) return "Pequeño";
    if (normalized <= PRINT_SIZE_MEDIUM_MAX_CM) return "Mediano";
    return "Grande";
};

export const getSelectionSizeLabel = (selection = null) =>
    isFixedPredesignSelection(selection)
        ? getPredesignSizeLabel(selection?.attrs?.predesign_size_tier)
        : getPrintSizeLabelFromCm(getSelectionEffectiveSizeCm(selection));

export const roundUpToHalfCm = (value) => {
    const normalized = parsePositiveNumberOrNull(value) || 0;
    return (Math.ceil((normalized * 2) - 1e-9) / 2) + 0.5;
};

export const formatCmValue = (value, digits = 1) => {
    const normalized = Number(value);
    if (!Number.isFinite(normalized)) return "";
    return normalized.toLocaleString("es-AR", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

export const formatPrintSizeCm = (value, { roundUpHalf = false } = {}) => {
    const normalized = roundUpHalf ? roundUpToHalfCm(value) : (parsePositiveNumberOrNull(value) || 0);
    return `${formatCmValue(normalized, 1)} cm`;
};

export const resolvePrintSelectionSide = (selection = null, fallback = null) => {
    const rawSide = selection?.attrs?.side;
    if (String(rawSide).trim() !== "") {
        return Number(rawSide) === 1 ? "back" : "front";
    }
    if (fallback === "back" || fallback === "front") return fallback;
    return null;
};

export const buildPrintOccupancyKey = (locationKey, side) =>
    `${String(locationKey || "").trim()}::${String(side || "").trim().toLowerCase()}`;

export const getPrintLocationKeyFromSelection = (printKey, selection = null) =>
    String(selection?.attrs?.location_key || printKey || "").split(PRINT_INSTANCE_SEPARATOR)[0];

export const resolvePrintPreviewUrl = (selection = null) => {
    const attrs = selection?.attrs || {};
    const print = selection?.print || {};
    const raw =
        attrs.image_url
        || attrs.img_full
        || attrs.img_url
        || print.icon_url
        || print.img_full
        || print.img_url
        || print.image_url
        || print.icon
        || print.img
        || null;
    if (!raw) return null;
    const source = String(raw || "").trim();
    if (!source || source.includes("undefined") || source.includes("null")) return null;
    if (source.startsWith("data:") || source.startsWith("blob:")) return source;
    if (source.startsWith("http")) {
        try {
            const url = new URL(source);
            if (url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "0.0.0.0") {
                return `${CDN_BASE}${url.pathname}${url.search}`;
            }
        } catch (error) {
            return source;
        }
        return source;
    }
    if (print.id && (source === String(print.icon || "") || source === String(print.img || ""))) {
        if (source === String(print.icon || "")) {
            return `${CDN_BASE}/uploads/charly/prints/${print.id}/icon/${source}`;
        }
        return `${CDN_BASE}/uploads/charly/prints/${print.id}/${source}`;
    }
    return `${CDN_BASE}${source.startsWith("/") ? "" : "/"}${source}`;
};

export const hasRealPrintContent = (selection = null) => {
    if (!selection?.print) return false;
    const attrs = selection?.attrs || {};
    const print = selection?.print || {};
    const mode = String(selection?.mode || "").toLowerCase();
    const printKind = String(attrs?.print_kind || "").toLowerCase();

    const textValue = String(attrs?.text || "").trim();
    if (mode === "text" || printKind === "text") {
        return textValue.length > 0;
    }

    const hasImageUrl = !!String(attrs?.image_url || "").trim();
    const hasPredesignAsset = !!String(
        print?.img_full
        || print?.img
        || print?.icon_url
        || print?.img_url
        || print?.image_url
        || ""
    ).trim();

    if (printKind === "image_upload") {
        return hasImageUrl;
    }
    if (printKind === "predesign" || mode === "predesign") {
        return hasPredesignAsset;
    }
    if (mode === "image") {
        return hasImageUrl || hasPredesignAsset;
    }

    return hasImageUrl || hasPredesignAsset || textValue.length > 0;
};

export const isVisibleDesignerNode = (node) => !!node && node?.Item?.display !== "none";

export const hasDesignerNodeImages = (node) => !!(
    node?.Item?.img_front_t
    || node?.Item?.img_front
    || node?.Item?.img_back_t
    || node?.Item?.img_back
    || node?.img_front
    || node?.img_front_shadow
    || node?.img_back
    || node?.img_back_shadow
);

export const DEBUG_ALWAYS_SHOW_PART_MODAL = false;
export const HIDDEN_EXTRA_PART_PATTERNS = ["serpentin", "recort"];
const STRIP_PART_PATTERN = /\btira(?:s)?\b/i;
export const DESIGNER_PART_VISIBILITY_RULES = Object.freeze([
  Object.freeze({
    groupPattern: "avios",
    partPattern: "cierre",
    partMatchMode: "exact",
    hide: true,
  }),
  Object.freeze({
    partPattern: "boton para chomba",
    partMatchMode: "exact",
    hide: true,
  }),
  Object.freeze({
    partPattern: "botones para chomba",
    partMatchMode: "exact",
    hide: true,
  }),
]);

export const normalizeSearchText = (value) => normalizeVisibilityText(value);

const matchesVisibilityPattern = (
  value,
  pattern,
  matchMode = "contains",
) => {
  if (pattern === undefined || pattern === null) return true;
  const rawValue = String(value || "");
  if (pattern instanceof RegExp) {
    const safeRegex = new RegExp(
      pattern.source,
      pattern.flags.replaceAll("g", ""),
    );
    return safeRegex.test(rawValue);
  }
  const normalizedValue = normalizeVisibilityText(rawValue);
  const normalizedPattern = normalizeVisibilityText(pattern);
  if (!normalizedPattern) return true;
  if (matchMode === "exact") {
    // 'contains' is default (leave untouched parts with partial matches), exact requires full match
    return normalizedValue === normalizedPattern;
  }
  return normalizedValue.includes(normalizedPattern);
};

const getNodeLabelCandidates = (node) => {
  const item = node?.Item || {};
  return [
    item?.nname,
    item?.name,
    item?.nice_name,
    node?.nname,
    node?.name,
    node?.nice_name,
  ];
};

export const shouldHideDesignerPart = ({
  groupLabel = "",
  partLabel = "",
  node = null,
} = {}) => {
  const candidates = [partLabel, ...getNodeLabelCandidates(node)];
  return DESIGNER_PART_VISIBILITY_RULES.some((rule) => {
    if (!rule || rule.hide === false) return false;
    const groupMatches = matchesVisibilityPattern(
      groupLabel,
      rule.groupPattern,
    );
    if (!groupMatches) return false;
    return candidates.some((candidate) =>
      matchesVisibilityPattern(
        candidate,
        rule.partPattern,
        rule.partMatchMode || "contains",
      ),
    );
  });
};

export const shouldHideExtraPartByName = (node) => {
    if (!node || Number(node.type) !== Number(MASTER_PART_TYPE.EXTRA)) return false;
    const item = node?.Item || {};
    const haystack = normalizeSearchText(
        `${item.nname || ""} ${item.name || ""} ${item.nice_name || ""} ${node.nice_name || ""} ${node.name || ""}`
    );
    if (!haystack) return false;
    return HIDDEN_EXTRA_PART_PATTERNS.some((pattern) => haystack.includes(pattern));
};

export const isStripePartNode = (node) => {
    if (!node) return false;
    const item = node?.Item || {};
    const haystack = normalizeSearchText(
        `${item.nname || ""} ${item.name || ""} ${item.nice_name || ""} ${node.nice_name || ""} ${node.name || ""}`
    );
    if (!haystack) return false;
    return STRIP_PART_PATTERN.test(haystack);
};

export const parseStripeQuantityFromLabel = (value) => {
    const text = normalizeSearchText(value);
    if (!text) return null;
    const patterns = [
        /(?:x|por)\s*(\d{1,2})\b/,
        /\b(\d{1,2})\s*tiras?\b/,
        /\btiras?\s*(\d{1,2})\b/,
    ];
    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (!match) continue;
        const qty = Number(match[1]);
        if (Number.isFinite(qty) && qty >= 1 && qty <= 6) return qty;
    }
    if (text.includes("simple")) return 1;
    if (text.includes("doble")) return 2;
    if (text.includes("triple")) return 3;
    return null;
};

export const parseStripeLocationFromLabel = (value) => {
    const text = normalizeSearchText(value);
    if (!text) return null;
    if (text.includes("diagonal") || text.includes("velez")) return "diagonal";
    if (text.includes("hombro")) return "hombro";
    if (text.includes("media manga")) return "manga";
    if (text.includes("manga completa")) return "manga";
    if (text.includes("manga") && text.includes("vertical")) return "manga_vertical";
    if (text.includes("manga")) return "manga";
    if (text.includes("torso") && text.includes("vertical")) return "torso_vertical";
    if (text.includes("torso")) return "torso";
    if (text.includes("frente") && text.includes("vertical")) return "torso_vertical";
    if (text.includes("frente")) return "torso";
    if (text.includes("tira")) return "general";
    return null;
};

export const collectSubtreeKeys = (node) => {
    const keys = [];
    const walk = (current) => {
        if (!current) return;
        keys.push(String(current.key));
        (current.children || []).forEach(walk);
    };
    walk(node);
    return keys;
};

export const dedupeNodesByKey = (nodes = []) => {
    const seen = new Set();
    return nodes.filter((node) => {
        const key = String(node?.key || "");
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const getVisibleNonPrintChildren = (nodes = []) => dedupeNodesByKey(
    nodes.filter((child) => isVisibleDesignerNode(child) && Number(child.type) !== PRINT_NODE_TYPE)
);

export const findFirstUsefulGroupLevel = (topNode) => {
    let currentLevel = getVisibleNonPrintChildren(topNode?.children || []);

    while (currentLevel.length > 0) {
        const selectorChildren = currentLevel.filter((child) => GROUP_OPTION_TYPES.has(Number(child.type)));
        if (selectorChildren.length > 0) {
            return dedupeNodesByKey(selectorChildren);
        }

        currentLevel = getVisibleNonPrintChildren(
            currentLevel.flatMap((child) => child?.children || [])
        );
    }

    return [];
};
